import { Grid, GridItem, Box, Text } from '@chakra-ui/react';
import { iconList } from 'devicon-react-svg';
import ChartEmptyState from '@/components/ChartEmptyState';
import ChartHeader from '@/components/ChartHeader';
import { trioUITheme } from '@/styles/theme';
import { TotalCandidatesByTechStack } from '@/types/api';
import { cleanStringSpaces } from '@/utils/global';
import { TechStackIcon } from './TalentPoolChart.styles';

export type TalentPoolChartProps = {
  title: string;
  tooltipMessage: string;
  data: TotalCandidatesByTechStack[];
};

const TalentPoolChart = ({ title, tooltipMessage, data }: TalentPoolChartProps): JSX.Element => {
  return (
    <Box
      data-testid="talent-pool-chart"
      shadow="0px 4px 6px rgba(0, 0, 0, 0.07)"
      mb="38px"
      py="38px"
      px="32px"
      bg="white"
      borderRadius="16px"
      maxW="100%"
    >
      <ChartHeader title={title} tooltipMessage={tooltipMessage} />
      {data.length > 0 ? (
        <Grid
          templateColumns="repeat(2, 1fr)"
          templateRows="repeat(6, 1fr)"
          autoFlow="column"
          gap={8}
          mt="16px"
          overflow="auto"
        >
          {data.map((stack: TotalCandidatesByTechStack) => {
            const hasTechIcon = iconList.some((icon) => icon === stack.name.toLowerCase());
            const techAbbreviation = `${stack.name[0]}${stack.name[1]}`;

            return (
              <GridItem
                data-testid={`tech-stack-${cleanStringSpaces(stack.name)}`}
                key={`${cleanStringSpaces(stack.name)}-${stack.totalCandidates}`}
              >
                <Box
                  bg={trioUITheme.colors['neutral-20']}
                  border={`1px solid ${trioUITheme.colors['neutral-30']}`}
                  borderRadius="4px"
                  px="8px"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  minW="200px"
                >
                  <Box display="flex" justifyContent="flex-start" alignItems="center">
                    {hasTechIcon ? (
                      <TechStackIcon icon={stack.name.toLowerCase()} viewBox="0 0 36 36" />
                    ) : (
                      <p>{techAbbreviation}</p>
                    )}
                    <Text ml="9px" fontSize={14}>
                      {stack.name}
                    </Text>
                  </Box>
                  <Box
                    data-testid={`tech-stack-${cleanStringSpaces(stack.name)}-total`}
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-end"
                    fontWeight={600}
                    fontSize={26}
                    paddingTop="4px"
                  >
                    {stack.totalCandidates}
                  </Box>
                </Box>
              </GridItem>
            );
          })}
        </Grid>
      ) : (
        <ChartEmptyState
          title="Your search didn’t match any result"
          subTitle="Try to choose different options."
        />
      )}
    </Box>
  );
};

export default TalentPoolChart;
